<template>
    <div>
        <div class="lang-selector">
            <div class="selected">{{ selectedLanguageDesc }}</div>
            <div>
                <div
                    v-for="(language,idx) in languages.filter(l => l.code !== currentLocale)"
                    :key="idx"
                    class="lang-entry">
                    <span v-if="idx > 0" class="divider">|</span>
                    <a
                        v-if="language.code !== selectedLocale"
                        href="javascript:void(0)"
                        @click="onLanguageChange(language.code)"
                        @enter="onLanguageChange(language.code)"
                        @space="onLanguageChange(language.code)">{{ language.desc }}</a>
                </div>
            </div>
        </div>
        <task-dialog
            v-if="feedbackMsg.length > 0"
            :message="feedbackMsg"
            @close="onFeedbackMsgDialogClose">
        </task-dialog>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";

import TaskDialog from "@/components/TaskDialog.vue";

import { AvailableLanguages } from "@/lang/common";
import { loadLanguageAsync } from "@/setup/i18n-setup";

interface ILanguageChoice {
    code: string;
    desc: string;
}

const { locale } = useI18n();

const languages = ref<ILanguageChoice[]>([]);
const selectedLocale = ref("");
const feedbackMsg = ref("");
const enabled = ref(false);

const currentLocale = computed(() => locale.value);

const selectedLanguageDesc = computed(() => {
    const lang = languages.value.find(l => l.code === locale.value);
    return lang !== undefined ? lang.desc : "";
});

function onFeedbackMsgDialogClose(): void {
    feedbackMsg.value = "";
}

function onLanguageChange(newLocale: string): void {
    enabled.value = false;

    const promise = loadLanguageAsync(newLocale);
    promise.then(() => {
        selectedLocale.value = newLocale;
        enabled.value = true;
    }).catch(() => {
        feedbackMsg.value = "languageSelect.unableToLoad";
        enabled.value = true;
    });
}

onBeforeMount(() => {
    const langs = Object.getOwnPropertyNames(AvailableLanguages);

    langs.forEach(lang => {
        languages.value.push({ code: lang, desc: AvailableLanguages[lang] });
    });

    selectedLocale.value = currentLocale.value;
    enabled.value = true;
});
</script>

<style scoped>
.lang-entry {
    display: inline;
    font-size: small;
}

.lang-selector {
    display: flex;
    flex-direction: column;
    user-select: none;
    margin: 0 .25em;
    align-items: flex-end;
}

.divider {
    min-width: 1em;
    display: inline-block;
    text-align: center;
    font-weight: lighter;
}

a {
    color: var(--ink-colour);
    font-weight: lighter;
    text-decoration-line: none;
}

a:hover {
    text-decoration-line: underline;
}
</style>
